import { useState } from 'react';
import { createContactSchema, type Contact } from '@faktorplus/shared';
import {
  useContacts,
  useCreateContact,
  useDeleteContact,
  useUpdateContact,
} from '../../api/contacts';
import { useVendors } from '../../api/dictionaries';
import { Button } from '../../components/ui/button';
import { EmptyState, ErrorBox, LoadingBlock } from '../../components/ui/feedback';
import { Field, Input, Select } from '../../components/ui/form';
import { PencilIcon } from '../../components/ui/icons';
import { Modal, modalStyles } from '../../components/ui/modal';
import { ApiError } from '../../lib/api-client';
import { plural } from '../../lib/format';
import pageStyles from '../../components/layout/page.module.css';

/**
 * The company address book (24.09.2026): the people behind the vendors,
 * entered by whoever met them, kept by the firm. Everybody reads, everybody
 * corrects; the phone number is the identity, so a duplicate answers with
 * the name it already belongs to.
 */
export function ContactsPage() {
  const { data: contacts = [], isLoading, error } = useContacts();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Contact | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const remove = useDeleteContact();

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBox>Nie udało się pobrać kontaktów.</ErrorBox>;

  const rows = filterContacts(contacts, search);

  return (
    <>
      <header className={pageStyles.header}>
        <h1 className={pageStyles.title}>Kontakty</h1>
        <span className={pageStyles.subtitle}>
          {contacts.length} {plural(contacts.length, ['kontakt', 'kontakty', 'kontaktów'])} ·
          wspólna książka adresowa firmy
        </span>
        <div className={pageStyles.actions}>
          <Button variant="primary" onClick={() => setIsCreating(true)}>
            + Nowy kontakt
          </Button>
        </div>
      </header>

      <Input
        placeholder="Szukaj po nazwisku, firmie, telefonie…"
        aria-label="Szukaj kontaktu"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        style={{ maxWidth: 420, marginBottom: 16 }}
      />

      {remove.error && (
        <ErrorBox>
          {remove.error instanceof ApiError
            ? remove.error.message
            : 'Nie udało się usunąć kontaktu.'}
        </ErrorBox>
      )}

      {rows.length === 0 ? (
        <EmptyState
          mark="∅"
          title="Brak kontaktów"
          description={
            search
              ? 'Nic nie pasuje do wyszukiwania. Zmień je albo wyczyść.'
              : 'Dodaj pierwszy kontakt — np. brygadzistę nowego podwykonawcy.'
          }
        />
      ) : (
        <div className={pageStyles.tableWrap}>
          <table className={pageStyles.table}>
            <thead>
              <tr>
                <th>Nazwisko i imię</th>
                <th>Telefon</th>
                <th className={pageStyles.hideOnPhone}>E-mail</th>
                <th className={pageStyles.hideOnPhone}>Firma</th>
                <th className={pageStyles.hideOnPhone}>Kontrahent</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((contact) => (
                <tr key={contact.id}>
                  <td className={pageStyles.strong}>
                    {contact.lastName} {contact.firstName}
                  </td>
                  <td>
                    {/* tel: — on a phone this is the whole point of the page. */}
                    <a href={`tel:${contact.phone}`} className={pageStyles.cellLink}>
                      {contact.phone}
                    </a>
                  </td>
                  <td className={`${pageStyles.muted} ${pageStyles.hideOnPhone}`}>
                    {contact.email ? (
                      <a href={`mailto:${contact.email}`} className={pageStyles.cellLink}>
                        {contact.email}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className={`${pageStyles.muted} ${pageStyles.hideOnPhone}`}>
                    {contact.companyName ?? '—'}
                  </td>
                  <td className={`${pageStyles.muted} ${pageStyles.hideOnPhone}`}>
                    {contact.vendorName ?? '—'}
                  </td>
                  <td>
                    <div className={pageStyles.rowActions}>
                      <Button
                        variant="soft"
                        icon={<PencilIcon />}
                        onClick={() => setEditing(contact)}
                      >
                        Edytuj
                      </Button>
                      <Button
                        variant="danger"
                        isLoading={remove.isPending && remove.variables === contact.id}
                        onClick={() => remove.mutate(contact.id)}
                      >
                        Usuń
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(isCreating || editing) && (
        <ContactDialog
          contact={editing}
          onClose={() => {
            setIsCreating(false);
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

function filterContacts(contacts: Contact[], search: string): Contact[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return contacts;

  return contacts.filter((contact) =>
    [
      contact.firstName,
      contact.lastName,
      contact.phone,
      contact.email ?? '',
      contact.companyName ?? '',
      contact.vendorName ?? '',
    ]
      .join(' ')
      .toLowerCase()
      .includes(needle),
  );
}

function ContactDialog({
  contact,
  onClose,
}: {
  contact: Contact | null;
  onClose: () => void;
}) {
  const create = useCreateContact();
  const update = useUpdateContact();
  const { data: vendors = [] } = useVendors();

  const [firstName, setFirstName] = useState(contact?.firstName ?? '');
  const [lastName, setLastName] = useState(contact?.lastName ?? '');
  const [phone, setPhone] = useState(contact?.phone ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [companyName, setCompanyName] = useState(contact?.companyName ?? '');
  const [vendorId, setVendorId] = useState(contact?.vendorId ?? '');
  const [validationError, setValidationError] = useState<string | null>(null);

  const isPending = create.isPending || update.isPending;
  const serverError = create.error ?? update.error;

  async function handleSubmit() {
    setValidationError(null);

    const parsed = createContactSchema.safeParse({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone,
      email,
      companyName,
      vendorId,
    });

    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Sprawdź wprowadzone dane');
      return;
    }

    if (contact) {
      await update.mutateAsync({ id: contact.id, input: parsed.data });
    } else {
      await create.mutateAsync(parsed.data);
    }
    onClose();
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={contact ? 'Edytuj kontakt' : 'Nowy kontakt'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Anuluj
          </Button>
          <span className={modalStyles.footerSpacer} />
          <Button variant="primary" isLoading={isPending} onClick={() => void handleSubmit()}>
            {contact ? 'Zapisz zmiany' : 'Dodaj kontakt'}
          </Button>
        </>
      }
    >
      <div className={modalStyles.row}>
        <Field label="Imię">
          {(id) => (
            <Input
              id={id}
              value={firstName}
              autoFocus={!contact}
              onChange={(event) => setFirstName(event.target.value)}
            />
          )}
        </Field>
        <Field label="Nazwisko">
          {(id) => (
            <Input
              id={id}
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
          )}
        </Field>
      </div>

      <Field
        label="Telefon"
        hint="Numer identyfikuje kontakt — drugi wpis z tym samym numerem zostanie odrzucony."
      >
        {(id) => (
          <Input
            id={id}
            type="tel"
            value={phone}
            placeholder="+48 601 234 567"
            onChange={(event) => setPhone(event.target.value)}
          />
        )}
      </Field>

      <Field label="E-mail" hint="Może się powtarzać — np. wspólny kontakt@firma.pl.">
        {(id) => (
          <Input
            id={id}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        )}
      </Field>

      <Field label="Firma">
        {(id) => (
          <Input
            id={id}
            value={companyName}
            placeholder="np. KONSTRUKCJE"
            onChange={(event) => setCompanyName(event.target.value)}
          />
        )}
      </Field>

      <Field
        label="Kontrahent"
        hint="Opcjonalnie — powiązanie ze słownikiem kontrahentów."
      >
        {(id) => (
          <Select id={id} value={vendorId} onChange={(event) => setVendorId(event.target.value)}>
            <option value="">— bez powiązania</option>
            {vendors.map((vendor) => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.name}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {validationError && <ErrorBox>{validationError}</ErrorBox>}
      {serverError && (
        <ErrorBox>
          {serverError instanceof ApiError ? serverError.message : 'Nie udało się zapisać.'}
        </ErrorBox>
      )}
    </Modal>
  );
}
